import React, { useState, useRef, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../context/App-Context";
// Adjust path as needed based on your folder structure

const TempleGridOcancerzinputform = () => {
  const auth = useContext(AuthContext);

  // --- STATE ---
  const [newPostInput, setNewPostInput] = useState("");
  const [isStellaPost, setIsStellaPost] = useState(false); // The 'tgrid' toggle
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- CAMERA STATE ---
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [latestCaptureId, setLatestCaptureId] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState("user");

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // --- HANDLERS ---
  const clearForm = () => {
    setNewPostInput("");
    setIsStellaPost(false);
    selectedPhotos.forEach((img) => {
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
    });
    setSelectedPhotos([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const validFiles = Array.from(e.target.files).filter((f) =>
      f.type.startsWith("image/")
    );
    const newImages = validFiles.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      previewUrl: URL.createObjectURL(file),
      timestamp: null,
    }));
    setSelectedPhotos((prev) => [...prev, ...newImages]);
  };

  const removePhoto = (id) => {
    setSelectedPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  // --- CAMERA LOGIC ---
  const openCamera = async () => {
    setCapturedPhoto(null);
    setLatestCaptureId(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: currentFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(console.error);
        }
      }, 150);
    } catch (err) {
      alert("Could not open camera: " + err.message);
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    setShowCamera(false);
    setCapturedPhoto(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    const ctx = canvas.getContext("2d");

    if (currentFacingMode === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob(
      (blob) => {
        const previewUrl = URL.createObjectURL(blob);
        let timestamp = null;

        if (isStellaPost) {
          const now = new Date();
          timestamp = new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
            month: "long",
            day: "numeric",
            year: "numeric",
          })
            .format(now)
            .toLowerCase();
        }

        const newImage = {
          id: Date.now() + Math.random(),
          file: new File([blob], `camera-${Date.now()}.jpg`, {
            type: "image/jpeg",
          }),
          previewUrl,
          timestamp,
        };

        setSelectedPhotos((prev) => [...prev, newImage]);
        setLatestCaptureId(newImage.id);
        setCapturedPhoto(previewUrl);
      },
      "image/jpeg",
      0.95
    );
  };

  const handleRetake = () => {
    setSelectedPhotos((prev) =>
      prev.filter((img) => img.id !== latestCaptureId)
    );
    setLatestCaptureId(null);
    setCapturedPhoto(null);
  };

  // --- SUBMIT LOGIC ---
  const handleSubmit = async () => {
    if (!newPostInput.trim() && selectedPhotos.length === 0) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      const imageTimestamps = selectedPhotos.map((img) => img.timestamp);

      const postObjectforSubmital = {
        ocancerzText: newPostInput,
        tgridFile: isStellaPost,
        imageTimestamps,
      };

      selectedPhotos.forEach((img) => {
        formData.append("StarPostImages", img.file);
      });

      formData.append("userUsername", auth.loginVar);
      formData.append(
        "templeGridNormalPostData",
        JSON.stringify(postObjectforSubmital)
      );

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/zeusapolis/ocancerz-metavz/createOcancerzoOSStarPost`,
        formData
      );

      alert("Post uploaded successfully!");
      clearForm();
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.05)",
        padding: "20px",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.1)",
        marginBottom: "20px",
      }}
    >
      {/* Stella Toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "rgba(255,255,255,0.1)",
          padding: "10px",
          borderRadius: "4px",
          marginBottom: "10px",
        }}
      >
        <span style={{ fontWeight: "bold", color: "white" }}>Star Post</span>
        <button
          type="button"
          onClick={() => setIsStellaPost(!isStellaPost)}
          style={{
            background: isStellaPost ? "#7b00ff" : "#ccc",
            border: "none",
            padding: "5px 15px",
            borderRadius: "15px",
            color: "white",
            cursor: "pointer",
            transition: "background 0.3s",
          }}
        >
          tgrid {isStellaPost ? "✔" : ""}
        </button>
      </div>

      {/* Main Text Input */}
      <textarea
        value={newPostInput}
        onChange={(e) => setNewPostInput(e.target.value)}
        placeholder="A Star..."
        rows={3}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: "4px",
          border: "1px solid #ccc",
          background: "white",
          color: "#00008b",
          resize: "vertical",
          boxSizing: "border-box",
        }}
      />

      {/* Photo Controls */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "space-between",
          marginTop: "10px",
        }}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: "white",
            border: "1px solid #7b00ff",
            color: "#7b00ff",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          🖼️ Photos
        </button>
        <button
          type="button"
          onClick={openCamera}
          style={{
            background: "white",
            border: "1px solid #7b00ff",
            color: "#7b00ff",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          📸 Camera
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </div>

      {/* Photo Previews */}
      {selectedPhotos.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "10px",
            overflowX: "auto",
            padding: "15px 0",
            marginTop: "10px",
          }}
        >
          {selectedPhotos.map((photo) => (
            <div
              key={photo.id}
              style={{
                position: "relative",
                minWidth: "60px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <img
                src={photo.previewUrl}
                alt="preview"
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <button
                onClick={() => removePhoto(photo.id)}
                style={{
                  position: "absolute",
                  top: -5,
                  right: -5,
                  background: "red",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                ×
              </button>
              {photo.timestamp && (
                <div
                  style={{
                    fontSize: "9px",
                    color: "#ccc",
                    marginTop: "4px",
                    textAlign: "center",
                    width: "80px",
                    wordWrap: "break-word",
                  }}
                >
                  {photo.timestamp.split(",")[0]}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Post Button */}
      {auth.isLoggedIn && (
        <button
          // onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            background: "#00008b",
            color: "white",
            border: "1px solid #7b00ff",
            padding: "10px",
            borderRadius: "4px",
            width: "100%",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            fontWeight: "bold",
            marginTop: "15px",
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting ? "Posting..." : `Post ${isStellaPost ? "tgrid" : ""}`}
        </button>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "15px",
              borderRadius: "8px",
              width: "100%",
              maxWidth: "500px",
            }}
          >
            <h3 style={{ color: "#333", margin: "0 0 15px 0" }}>Camera</h3>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                borderRadius: "8px",
                background: "black",
                transform: currentFacingMode === "user" ? "scaleX(-1)" : "none",
                display: !capturedPhoto ? "block" : "none",
              }}
            />
            {capturedPhoto && (
              <img
                src={capturedPhoto}
                alt="captured"
                style={{ width: "100%", borderRadius: "8px", display: "block" }}
              />
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                marginTop: "15px",
                flexWrap: "wrap",
              }}
            >
              {!capturedPhoto ? (
                <>
                  <button
                    onClick={closeCamera}
                    style={{
                      padding: "8px 15px",
                      borderRadius: "4px",
                      border: "1px solid gray",
                      background: "white",
                      color: "blue",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={capturePhoto}
                    style={{
                      padding: "8px 15px",
                      borderRadius: "4px",
                      background: "#7b00ff",
                      color: "white",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    📸 TAKE PHOTO
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleRetake}
                    style={{
                      padding: "8px 15px",
                      borderRadius: "4px",
                      border: "1px solid gray",
                      background: "white",
                      color: "blue",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    Retake
                  </button>
                  <button
                    onClick={closeCamera}
                    style={{
                      padding: "8px 15px",
                      borderRadius: "4px",
                      border: "1px solid gray",
                      background: "white",
                      color: "blue",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    ✅ Use Photo
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TempleGridOcancerzinputform;
